import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  CalendarClock,
  CircleCheck,
  CircleHelp,
  CircleX,
  Sparkles,
  TriangleAlert,
} from 'lucide-react'

import {
  pmsPresentation,
  type AppointmentItem,
} from '../../appointments/appointments-model'
import {
  appointmentLaneKey,
  appointmentMinute,
  appointmentTimeLabel,
  isBookedByFlio,
  laneLabel,
  minuteOfDay,
  type CalendarGrouping,
} from '../calendar-model'
import {
  clockLabel,
  displayRange,
  laneColumns,
  placeLane,
  unavailableIntervals,
  UNKNOWN_DURATION_MINUTES,
  type TimedEntry,
} from '../calendar-layout'
import type { ClinicDayHours } from '../clinic-hours'
import { LaneAvatar } from './LaneAvatar'
import styles from './CalendarDayView.module.css'

/** Pixels per minute: a 20-minute visit is 32px, two readable lines. */
export const DAY_MINUTE_HEIGHT = 1.6
const LANE_COLUMN_REM = 6
const NOW_REFRESH_MS = 60_000

export type AppointmentTone =
  'confirmed' | 'scheduled' | 'cancelled' | 'unknown'

export function appointmentTone(item: AppointmentItem): AppointmentTone {
  return item.status === 'confirmed' ||
    item.status === 'scheduled' ||
    item.status === 'cancelled'
    ? item.status
    : 'unknown'
}

const TONE_ICONS = {
  confirmed: CircleCheck,
  scheduled: CalendarClock,
  cancelled: CircleX,
  unknown: CircleHelp,
} as const

function durationMinutes(item: AppointmentItem) {
  if (item.scheduledAtMs === null || item.scheduledEndMs === null) return null
  const minutes = (item.scheduledEndMs - item.scheduledAtMs) / 60_000
  return minutes > 0 ? minutes : null
}

function timedEntry(item: AppointmentItem): TimedEntry<AppointmentItem> | null {
  const start = appointmentMinute(item)
  if (start === null) return null
  const duration = durationMinutes(item)
  return {
    item,
    startMinute: start,
    endMinute: duration === null ? null : start + duration,
  }
}

function AppointmentCard({
  item,
  grouping,
  nowMs,
  onSelect,
}: {
  item: AppointmentItem
  grouping: CalendarGrouping
  nowMs: number
  onSelect: () => void
}) {
  const tone = appointmentTone(item)
  const StatusIcon = TONE_ICONS[tone]
  const flio = isBookedByFlio(item)
  const pms = pmsPresentation(item, nowMs)
  const place = grouping === 'room' ? item.providerName : item.operatoryName
  const time = appointmentTimeLabel(item)
  const meta = [time, item.appointmentTypeName, place].filter(
    (value): value is string => Boolean(value)
  )
  const description = [
    time,
    item.patientDisplayName,
    item.appointmentTypeName,
    grouping === 'room' ? item.providerName : null,
    item.operatoryName,
    item.statusLabel,
    flio ? 'Booked by Flio' : null,
    pms?.needsAttention ? pms.label : null,
  ]
    .filter((value): value is string => Boolean(value))
    .join(', ')
  return (
    <button
      type="button"
      className={styles.card}
      data-tone={tone}
      data-attention={pms?.needsAttention ? 'true' : undefined}
      aria-label={description}
      title={description}
      onClick={onSelect}
    >
      <span className={styles.cardName}>
        <strong>{item.patientDisplayName}</strong>
        <StatusIcon className={styles.toneIcon} size={12} aria-hidden="true" />
        {pms?.needsAttention ? (
          <TriangleAlert
            className={styles.attentionIcon}
            size={12}
            aria-hidden="true"
          />
        ) : null}
        {flio ? (
          <Sparkles className={styles.flioIcon} size={12} aria-hidden="true" />
        ) : null}
      </span>
      <span className={styles.cardMeta}>{meta.join(' · ')}</span>
      <span className={styles.cardStatus}>{item.statusLabel}</span>
    </button>
  )
}

export function CalendarDayView({
  date,
  appointments,
  lanes,
  grouping,
  dayHours,
  timeZone,
  timeZoneLabel,
  isToday,
  now,
  focusLane,
  empty,
  onSelectAppointment,
}: {
  date: string
  appointments: AppointmentItem[]
  /** Lane keys in display order. */
  lanes: string[]
  grouping: CalendarGrouping
  dayHours: ClinicDayHours
  timeZone: string | null
  timeZoneLabel: string | null
  isToday: boolean
  now: () => number
  focusLane: string | null
  empty: ReactNode
  onSelectAppointment: (item: AppointmentItem) => void
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const [nowMs, setNowMs] = useState(now)
  useEffect(() => {
    if (!isToday) return
    const timer = window.setInterval(() => setNowMs(now()), NOW_REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [isToday, now])

  const entries = useMemo(
    () =>
      appointments.flatMap((item) => {
        const entry = timedEntry(item)
        return entry ? [entry] : []
      }),
    [appointments]
  )
  const range = useMemo(() => displayRange(entries), [entries])
  const placedLanes = useMemo(
    () =>
      lanes.map((lane) => {
        const placed = placeLane(
          entries.filter(
            (entry) => appointmentLaneKey(entry.item, grouping) === lane
          )
        )
        return { lane, placed, columns: laneColumns(placed) }
      }),
    [entries, grouping, lanes]
  )
  const height = (range.endMinute - range.startMinute) * DAY_MINUTE_HEIGHT
  const offset = (minute: number) =>
    (minute - range.startMinute) * DAY_MINUTE_HEIGHT
  const hours = Array.from(
    { length: (range.endMinute - range.startMinute) / 60 + 1 },
    (_, index) => range.startMinute + index * 60
  )
  const gaps = unavailableIntervals(dayHours, range)
  const clinicMinute = isToday ? minuteOfDay(nowMs, timeZone) : null
  const nowMinute =
    clinicMinute !== null &&
    clinicMinute >= range.startMinute &&
    clinicMinute <= range.endMinute
      ? clinicMinute
      : null
  const gridStyle = {
    '--lane-template': placedLanes
      .map(
        ({ columns }) => `minmax(${columns * LANE_COLUMN_REM}rem, ${columns}fr)`
      )
      .join(' '),
    '--hour-height': `${60 * DAY_MINUTE_HEIGHT}px`,
    '--lanes-min-width': `${placedLanes.reduce((total, { columns }) => total + columns * LANE_COLUMN_REM, 0)}rem`,
  } as CSSProperties

  // Open each day where the work is: just before now on today, else at the
  // first visit (the grid keeps its scroll while the same day re-renders).
  const firstMinute = entries.reduce(
    (first, entry) => Math.min(first, entry.startMinute),
    Infinity
  )
  const target =
    nowMinute !== null
      ? nowMinute - 45
      : Number.isFinite(firstMinute)
        ? firstMinute - 15
        : range.startMinute
  const scrollTarget = Math.max(0, offset(target))
  const scrolledFor = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (!scroller.current || scrolledFor.current === date) return
    scrolledFor.current = date
    scroller.current.scrollTop = scrollTarget
  }, [date, scrollTarget])

  useEffect(() => {
    if (!focusLane || !scroller.current) return
    Array.from(scroller.current.querySelectorAll<HTMLElement>('[data-lane]'))
      .find((node) => node.dataset.lane === focusLane)
      ?.scrollIntoView?.({ block: 'nearest', inline: 'center' })
  }, [focusLane])

  return (
    <section className={styles.day} aria-label="Provider day schedule">
      <div
        className={styles.scroller}
        ref={scroller}
        // With no visits there is nothing to tab to, yet the hours still scroll.
        tabIndex={appointments.length ? undefined : 0}
      >
        <div className={styles.grid} style={gridStyle}>
          <div className={styles.corner}>
            {timeZoneLabel ? (
              <span title="Times are in the clinic’s time zone">
                {timeZoneLabel}
              </span>
            ) : null}
          </div>
          {placedLanes.map(({ lane, placed }, index) => (
            <div
              className={styles.laneHeader}
              data-first={index === 0 ? 'true' : undefined}
              data-lane={lane}
              data-focused={lane === focusLane ? 'true' : undefined}
              key={lane}
              title={laneLabel(lane)}
              style={{ gridRow: 1, gridColumn: index + 2 }}
            >
              {appointments.length ? (
                <>
                  <LaneAvatar label={laneLabel(lane)} grouping={grouping} />
                  <strong>{laneLabel(lane)}</strong>
                  <span className={styles.laneCount}>
                    {placed.length}
                    <span className={styles.srOnly}>
                      {placed.length === 1 ? ' appointment' : ' appointments'}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          ))}
          <div className={styles.gutter} style={{ height }} aria-hidden="true">
            {hours.map((minute) => (
              <span key={minute} style={{ top: offset(minute) }}>
                {clockLabel(minute)}
              </span>
            ))}
            {nowMinute !== null ? (
              <time
                className={styles.nowLabel}
                style={{ top: offset(nowMinute) }}
              >
                {clockLabel(nowMinute, { compact: true })}
              </time>
            ) : null}
          </div>
          {placedLanes.map(({ lane, placed }, index) => (
            <div
              className={styles.lane}
              data-first={index === 0 ? 'true' : undefined}
              data-focused={lane === focusLane ? 'true' : undefined}
              style={{ height, gridRow: 2, gridColumn: index + 2 }}
              key={lane}
              role="list"
              aria-label={laneLabel(lane)}
            >
              {gaps.map((gap) => (
                <span
                  className={styles.unavailable}
                  key={`${gap.startMinute}-${gap.endMinute}`}
                  data-unavailable={`${clockLabel(gap.startMinute)} to ${clockLabel(gap.endMinute)}`}
                  aria-hidden="true"
                  style={{
                    top: offset(gap.startMinute),
                    height:
                      (gap.endMinute - gap.startMinute) * DAY_MINUTE_HEIGHT,
                  }}
                />
              ))}
              {placed.map(
                ({ item, startMinute, endMinute, column, columns }) => (
                  <div
                    className={styles.placed}
                    role="listitem"
                    key={item.appointmentId}
                    style={
                      {
                        top: offset(startMinute) + 1,
                        height:
                          endMinute === null
                            ? undefined
                            : Math.round(
                                (endMinute - startMinute) *
                                  DAY_MINUTE_HEIGHT *
                                  10
                              ) / 10,
                        minHeight:
                          endMinute === null
                            ? UNKNOWN_DURATION_MINUTES * DAY_MINUTE_HEIGHT
                            : undefined,
                        '--column': column,
                        '--columns': columns,
                      } as CSSProperties
                    }
                  >
                    <AppointmentCard
                      item={item}
                      grouping={grouping}
                      nowMs={nowMs}
                      onSelect={() => onSelectAppointment(item)}
                    />
                  </div>
                )
              )}
            </div>
          ))}
          {nowMinute !== null ? (
            <div
              className={styles.nowLayer}
              style={{ height }}
              aria-hidden="true"
            >
              <span
                className={styles.nowLine}
                style={{ top: offset(nowMinute) }}
              />
            </div>
          ) : null}
        </div>
      </div>
      {empty ? <div className={styles.empty}>{empty}</div> : null}
    </section>
  )
}
